import React from 'react';

import styles from './SonarTableChart.module.css';

import { cssvar } from '../../../services/charts';
import { getScoreParameters, getScorePolls, getSheetParametersPoll } from '../../../services/Patient';
import { getCVParams, getScore } from '../../../services/General';

import PatientsContext from '../../../context/PatientsContext';
import ColumnsContext from '../../../context/ColumnsContext';

import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import {
    LineChart,
} from 'echarts/charts';
import {
    GridComponent,
    TooltipComponent,
} from 'echarts/components';
import {
    CanvasRenderer,
} from 'echarts/renderers';

import moment from 'moment';
import ScoresContext from '../../../context/ScoresContext';

interface values {
    value?: number,
    symbol?: string,
    date: string
    symbolSize: number
}

const SonarTableChart: React.FC<{
    case_id: number | undefined,
    patientID: number
    generalInfo?: boolean,
}> = ({ case_id, patientID, generalInfo }) => {

    const { t } = useTranslation();

    const { physiologicalData } = useContext(PatientsContext);
    const { cellsSonarSelected } = useContext(ColumnsContext);
    const { scores } = useContext(ScoresContext);

    const dateFormat = (fecha: string) =>
        moment(fecha, 'DD-MM-YYYY HH:mm').format('DD-MM[\n]HH:mm');

    const cellID = cellsSonarSelected[patientID];

    const chartOption = useMemo(() => {

        const calcularDataXAxis = (data: values[]) => {
            const dataValues: string[] = [];
            for (let i = 0; i < data.length; i++) {
                dataValues.push(dateFormat(data[i].date));
            }
            return dataValues;
        };

        const calcularDataXAxisDays = (data: values[]) => {
            const dataValues: string[] = [];
            for (let i = 0; i < data.length; i++) {
                dataValues.push(dateFormat(data[i].date).slice(0, 5));
            }
            return dataValues;
        };

        const paramConfig = getCVParams(cellID);
        const score = getScore(cellID, scores);

        const data = physiologicalData.find(caseT => caseT.case_id === case_id);
        let value_days: values[] = [];

        if (data === undefined) return {};

        if (paramConfig === null) {
            if (score) {
                if (score?.score_parameters?.length > 0) {
                    value_days = getScoreParameters(data.elements, score.score_parameters).map(scoreValue => ({
                        value: scoreValue.value,
                        symbol: 'circle',
                        date: scoreValue.date,
                        symbolSize: 3
                    }));
                }
                if (score.inquiry_id !== null && score.inquiry_id !== undefined) {
                    const sheetParameterIDs = getSheetParametersPoll(data.elements, score.inquiry_id);
                    value_days = getScorePolls(data.elements, sheetParameterIDs).map(scoreValue => ({
                        value: scoreValue.value,
                        symbol: 'circle',
                        date: scoreValue.date,
                        symbolSize: 3
                    }));
                }
            }
        }
        else if (paramConfig.sheet_parameter_id) {
            data.elements.forEach(timestamp => {
                if (!Number.isNaN(Number(timestamp.values[paramConfig.sheet_parameter_id])))
                    value_days.push({
                        value: Number(timestamp.values[paramConfig.sheet_parameter_id]),
                        symbol: 'circle',
                        date: timestamp.date,
                        symbolSize: 3
                    });
            });
            value_days = value_days.filter(it => it !== null);
        }

        const xAxis = generalInfo ? {
            type: 'category',
            data: calcularDataXAxis(value_days).reverse(),
            axisTick: { show: true, length: 5 },
            axisLabel: { show: true, fontSize: 12 }
        } : {
            type: 'category',
            data: calcularDataXAxisDays(value_days).reverse(),
            silent: true,
            axisTick: { show: true, length: 3 },
            axisLabel: { show: true, fontSize: 8 },
        };

        const yAxis = {
            axisLabel: { fontSize: generalInfo ? 12 : 8 },
            axisLine: { show: true },
            axisTick: { show: true, length: 3 },
            scale: true,
            min: generalInfo ?
                (value: { min: number, max: number; }) =>
                    Math.trunc(value.min / 10) * 10
                : null,
            max: generalInfo ?
                (value: { min: number, max: number; }) =>
                    value.max < 10 ? value.max + 1 : Math.ceil(value.max / 10) * 10
                : null,
            splitNumber: generalInfo ? null : 2
        };

        const tooltip = generalInfo ? {
            trigger: 'axis',
            axisPointer: {
                type: 'shadow',
                shadowStyle: {
                    color: 'rgba(0, 0, 0, 0.03)',
                }
            },
        } : undefined;

        const series = generalInfo ? [
            {
                data: value_days.reverse(),
                type: 'line',
                connectNulls: true,
                name: t(paramConfig?.name || score?.name || ''),
                symbol: 'circle',
                symbolSize: [5, 5],
                showSymbol: true,
                lineStyle: {
                    color: cssvar('--primary-color'),
                    width: 1,
                    cap: 'round',
                },
            },
        ] : [
            {
                data: value_days.reverse(),
                type: 'line',
                lineStyle: {
                    color: cssvar('--primary-color'),
                    width: 1,
                    cap: 'round',
                },
            },
        ];

        const grid = generalInfo ?
            { left: 35, top: 10, right: 5, bottom: 30 }
            :
            { left: 30, top: 7, right: 0, bottom: 15 };

        return {
            xAxis,
            yAxis,
            tooltip,
            series,
            textStyle: { fontFamily: 'MontserratRegular' },
            grid
        };
    }, [case_id, cellID, generalInfo, physiologicalData, scores, t]);

    const classes = generalInfo ? styles.generalInfoCVChart : styles.sonarTableChart;

    echarts.use([LineChart, GridComponent, TooltipComponent, CanvasRenderer]);

    return (
        <div className={classes}>
            <ReactEChartsCore
                echarts={echarts}
                style={{ height: '100%', width: '100%' }}
                notMerge={true}
                lazyUpdate={true}
                option={chartOption} />
        </div>
    );
};

export default SonarTableChart;